/**
 * CDM Replay / Time Travel — point-in-time queries over the CDM event log.
 *
 * Three modes:
 *  1. Asset replay — for one golden_id + as_of, show every field's
 *     state on that date with source / observed_at / recorded_at, plus
 *     a one-click "compare to now" diff.
 *  2. Event firehose — auditor view, scrollable timeline of every
 *     event between two timestamps with kind / source / archetype
 *     filters.
 *  3. Diff — golden_id + two timestamps, side-by-side.
 *
 * Pure read view over Track 4.16 helpers. Banks asked: "what did we
 * believe about asset X on the day of the breach?". This page answers.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Clock, History, RotateCw, ChevronLeft, RefreshCw, Search,
  AlertTriangle, ArrowRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type Mode = 'asset' | 'firehose' | 'diff'

interface FieldState {
  value: any
  source: string
  confidence: number
  role: string
  observed_at: string | null
  recorded_at: string | null
}

interface GoldenAtResponse {
  golden_id: string
  as_of: string
  fields: Record<string, FieldState>
}

interface EventRow {
  id: string
  kind: string
  source: string
  source_id: string | null
  archetype: string
  golden_id: string | null
  field: string | null
  canonical_value: any
  confidence: number
  role: string
  occurred_at: string | null
  observed_at: string | null
  recorded_at: string | null
}

interface EventWindowResponse {
  count: number
  since: string
  until: string
  events: EventRow[]
}

interface DiffChange {
  field: string
  from: any
  to: any
  from_source: string | null
  to_source: string | null
  from_recorded_at: string | null
  to_recorded_at: string | null
}

interface DiffResponse {
  golden_id: string
  t1: string
  t2: string
  fields_added: string[]
  fields_removed: string[]
  fields_changed: DiffChange[]
  fields_unchanged: string[]
  state_t1: Record<string, FieldState>
  state_t2: Record<string, FieldState>
}

// Format a Date for an <input type="datetime-local"> element (local TZ).
const toLocalInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

// Convert a datetime-local value to ISO-8601 with timezone (UTC).
const toIso = (local: string): string => {
  if (!local) return ''
  // datetime-local has no timezone; treat as local then convert to UTC.
  return new Date(local).toISOString()
}

const ROLE_TONE: Record<string, string> = {
  observation:     'bg-blue/10 text-blue-light border-blue/30',
  human_assertion: 'bg-purple/10 text-purple-light border-purple/30',
  verification:    'bg-emerald/10 text-emerald border-emerald/30',
  agent_inference: 'bg-amber/10 text-amber-light border-amber/30',
  writeback_intent: 'bg-cyan/10 text-cyan border-cyan/30',
  writeback_executed: 'bg-purple/15 text-purple-light border-purple/30',
}

const formatValue = (v: any): string => {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  try { return JSON.stringify(v) } catch { return String(v) }
}

export default function CdmReplayPage() {
  const [mode, setMode] = useState<Mode>('asset')

  // Default time anchors: now, and 7 days ago.
  const now = useMemo(() => new Date(), [])
  const weekAgo = useMemo(() => new Date(Date.now() - 7 * 24 * 3600 * 1000), [])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <History size={18} className="text-blue-light" /> REPLAY · TIME TRAVEL
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          point-in-time projection · event firehose · state diff
        </span>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <ModeButton active={mode === 'asset'}    label="Asset replay"   icon={Clock}   onClick={() => setMode('asset')} />
        <ModeButton active={mode === 'firehose'} label="Event firehose" icon={History} onClick={() => setMode('firehose')} />
        <ModeButton active={mode === 'diff'}     label="State diff"     icon={RotateCw} onClick={() => setMode('diff')} />
      </div>

      {mode === 'asset'    && <AssetReplay defaultAsOf={weekAgo} />}
      {mode === 'firehose' && <EventFirehose defaultSince={weekAgo} defaultUntil={now} />}
      {mode === 'diff'     && <StateDiff defaultT1={weekAgo} defaultT2={now} />}

      <p className="text-[10px] text-text-muted text-center">
        Read-only projection over the cdm_events firehose · Track 4.16 ·
        every field is bitemporal (observed_at vs recorded_at)
      </p>
    </div>
  )
}

function ModeButton({
  active, label, icon: Icon, onClick,
}: {
  active: boolean
  label: string
  icon: React.ElementType
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded border font-medium transition',
        active
          ? 'border-blue/60 bg-blue/15 text-blue-light'
          : 'border-border bg-surface-card text-text-secondary hover:text-text-primary hover:border-border-strong',
      )}
    >
      <Icon size={12} /> {label}
    </button>
  )
}

// ── Mode 1: asset replay ─────────────────────────────────────────────────────

function AssetReplay({ defaultAsOf }: { defaultAsOf: Date }) {
  const [goldenId, setGoldenId] = useState('')
  const [asOf, setAsOf] = useState(toLocalInput(defaultAsOf))
  const [data, setData] = useState<GoldenAtResponse | null>(null)
  const [diff, setDiff] = useState<DiffResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const load = async () => {
    if (!goldenId) { setError('Enter a golden_id.'); return }
    setLoading(true)
    setError('')
    setDiff(null)
    try {
      const { data: d } = await api.get<GoldenAtResponse>(
        `/api/cdm/replay/golden/${goldenId}`,
        { params: { as_of: toIso(asOf) } },
      )
      setData(d)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Lookup failed.')
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  const compareToNow = async () => {
    if (!goldenId) return
    setLoading(true)
    setError('')
    try {
      const { data: d } = await api.get<DiffResponse>(
        `/api/cdm/replay/diff/${goldenId}`,
        { params: { t1: toIso(asOf), t2: new Date().toISOString() } },
      )
      setDiff(d)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Diff failed.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Golden ID</span>
            <input
              type="text"
              value={goldenId}
              onChange={e => setGoldenId(e.target.value.trim())}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-[340px] px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded focus:border-blue/60 focus:outline-none"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">As of (local)</span>
            <input
              type="datetime-local"
              value={asOf}
              onChange={e => setAsOf(e.target.value)}
              className="px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded focus:border-blue/60 focus:outline-none"
            />
          </label>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <Search size={11} /> {loading ? 'Loading…' : 'Project state'}
          </button>
          <button type="button" onClick={compareToNow} disabled={loading || !data} className="btn-ghost"
                  title="Show fields that changed between as-of and now">
            <RotateCw size={11} /> Compare to now
          </button>
        </div>
        {error && (
          <div className="text-[11px] text-rose-light flex items-center gap-1.5">
            <AlertTriangle size={11} /> {error}
          </div>
        )}
      </div>

      {data && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-2">
            State at {new Date(data.as_of).toLocaleString()}
            <span className="ml-2 text-[10px] text-text-muted font-normal">
              ({Object.keys(data.fields).length} field{Object.keys(data.fields).length === 1 ? '' : 's'})
            </span>
          </h2>
          {Object.keys(data.fields).length === 0 ? (
            <p className="text-xs text-text-muted">No projected fields — either the asset doesn't exist or no observations existed at that time.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-surface-raised">
                <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                  <th className="text-left px-2 py-1.5">Field</th>
                  <th className="text-left px-2 py-1.5">Value</th>
                  <th className="text-left px-2 py-1.5">Source</th>
                  <th className="text-left px-2 py-1.5">Role</th>
                  <th className="text-left px-2 py-1.5">Observed</th>
                  <th className="text-left px-2 py-1.5">Recorded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {Object.entries(data.fields).map(([f, s]) => (
                  <tr key={f}>
                    <td className="px-2 py-1.5 font-mono text-text-primary">{f}</td>
                    <td className="px-2 py-1.5 font-mono text-text-secondary">{formatValue(s.value)}</td>
                    <td className="px-2 py-1.5 text-text-secondary">{s.source}</td>
                    <td className="px-2 py-1.5">
                      <span className={clsx('badge text-[9px] border', ROLE_TONE[s.role] || 'border-border text-text-muted')}>{s.role}</span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                      {s.observed_at && new Date(s.observed_at).toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                      {s.recorded_at && new Date(s.recorded_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {diff && <DiffPanel diff={diff} />}
    </div>
  )
}

// ── Mode 2: event firehose ───────────────────────────────────────────────────

function EventFirehose({ defaultSince, defaultUntil }: { defaultSince: Date; defaultUntil: Date }) {
  const [since, setSince] = useState(toLocalInput(defaultSince))
  const [until, setUntil] = useState(toLocalInput(defaultUntil))
  const [kind, setKind] = useState('')
  const [source, setSource] = useState('')
  const [archetype, setArchetype] = useState('')
  const [goldenId, setGoldenId] = useState('')
  const [data, setData] = useState<EventWindowResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, string> = {
        since: toIso(since),
        until: toIso(until),
      }
      if (kind)      params.kind = kind
      if (source)    params.source = source
      if (archetype) params.archetype = archetype
      if (goldenId)  params.golden_id = goldenId
      const { data: d } = await api.get<EventWindowResponse>('/api/cdm/replay/events', { params })
      setData(d)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Query failed.')
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Since</span>
            <input type="datetime-local" value={since} onChange={e => setSince(e.target.value)}
                   className="px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Until</span>
            <input type="datetime-local" value={until} onChange={e => setUntil(e.target.value)}
                   className="px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Kind</span>
            <input type="text" value={kind} onChange={e => setKind(e.target.value)} placeholder="any"
                   className="w-32 px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Source</span>
            <input type="text" value={source} onChange={e => setSource(e.target.value)} placeholder="any"
                   className="w-32 px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Archetype</span>
            <input type="text" value={archetype} onChange={e => setArchetype(e.target.value)} placeholder="any"
                   className="w-36 px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Golden ID</span>
            <input type="text" value={goldenId} onChange={e => setGoldenId(e.target.value.trim())} placeholder="any"
                   className="w-[260px] px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> {loading ? 'Loading…' : 'Query'}
          </button>
        </div>
        {error && (
          <div className="text-[11px] text-rose-light flex items-center gap-1.5">
            <AlertTriangle size={11} /> {error}
          </div>
        )}
      </div>

      {data && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-2">
            {data.count.toLocaleString()} event{data.count === 1 ? '' : 's'} between
            {' '}{new Date(data.since).toLocaleString()}{' → '}{new Date(data.until).toLocaleString()}
          </h2>
          {data.count === 0 ? (
            <p className="text-xs text-text-muted">No events matched.</p>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="bg-surface-raised sticky top-0">
                  <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                    <th className="text-left px-2 py-1.5">Recorded</th>
                    <th className="text-left px-2 py-1.5">Observed</th>
                    <th className="text-left px-2 py-1.5">Kind</th>
                    <th className="text-left px-2 py-1.5">Source</th>
                    <th className="text-left px-2 py-1.5">Archetype</th>
                    <th className="text-left px-2 py-1.5">Golden ID</th>
                    <th className="text-left px-2 py-1.5">Field</th>
                    <th className="text-left px-2 py-1.5">Value</th>
                    <th className="text-left px-2 py-1.5">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.events.map(e => (
                    <tr key={e.id} className="hover:bg-surface-raised/40">
                      <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                        {e.recorded_at && new Date(e.recorded_at).toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                        {e.observed_at && new Date(e.observed_at).toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-text-primary">{e.kind}</td>
                      <td className="px-2 py-1.5 text-text-secondary">{e.source}</td>
                      <td className="px-2 py-1.5 text-text-secondary">{e.archetype}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-text-muted">
                        {e.golden_id ? (
                          <Link to={`/app/cdm/investigation/${e.golden_id}`} className="hover:text-blue-light">
                            {e.golden_id.slice(0, 8)}…
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-text-secondary">{e.field || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-text-secondary truncate max-w-[200px]">{formatValue(e.canonical_value)}</td>
                      <td className="px-2 py-1.5">
                        <span className={clsx('badge text-[9px] border', ROLE_TONE[e.role] || 'border-border text-text-muted')}>{e.role}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Mode 3: state diff ───────────────────────────────────────────────────────

function StateDiff({ defaultT1, defaultT2 }: { defaultT1: Date; defaultT2: Date }) {
  const [goldenId, setGoldenId] = useState('')
  const [t1, setT1] = useState(toLocalInput(defaultT1))
  const [t2, setT2] = useState(toLocalInput(defaultT2))
  const [data, setData] = useState<DiffResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const load = async () => {
    if (!goldenId) { setError('Enter a golden_id.'); return }
    setLoading(true)
    setError('')
    try {
      const { data: d } = await api.get<DiffResponse>(
        `/api/cdm/replay/diff/${goldenId}`,
        { params: { t1: toIso(t1), t2: toIso(t2) } },
      )
      setData(d)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Diff failed.')
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">Golden ID</span>
            <input type="text" value={goldenId} onChange={e => setGoldenId(e.target.value.trim())}
                   placeholder="00000000-0000-0000-0000-000000000000"
                   className="w-[340px] px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">From (t1)</span>
            <input type="datetime-local" value={t1} onChange={e => setT1(e.target.value)}
                   className="px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <ArrowRight size={14} className="text-text-muted mb-1.5" />
          <label className="space-y-1">
            <span className="block text-[10px] uppercase tracking-widest text-text-muted">To (t2)</span>
            <input type="datetime-local" value={t2} onChange={e => setT2(e.target.value)}
                   className="px-2 py-1.5 text-xs font-mono bg-surface-raised border border-border rounded" />
          </label>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RotateCw size={11} /> {loading ? 'Loading…' : 'Diff'}
          </button>
        </div>
        {error && (
          <div className="text-[11px] text-rose-light flex items-center gap-1.5">
            <AlertTriangle size={11} /> {error}
          </div>
        )}
      </div>

      {data && <DiffPanel diff={data} />}
    </div>
  )
}

function DiffPanel({ diff }: { diff: DiffResponse }) {
  const totalChanged = diff.fields_added.length + diff.fields_removed.length + diff.fields_changed.length
  return (
    <div className="card p-4 space-y-3">
      <h2 className="text-sm font-semibold text-text-primary">
        Diff {new Date(diff.t1).toLocaleString()} → {new Date(diff.t2).toLocaleString()}
        <span className="ml-2 text-[10px] text-text-muted font-normal">
          ({totalChanged} change{totalChanged === 1 ? '' : 's'} · {diff.fields_unchanged.length} unchanged)
        </span>
      </h2>

      {totalChanged === 0 ? (
        <p className="text-xs text-text-muted">No changes between these timestamps.</p>
      ) : (
        <div className="space-y-3">
          {diff.fields_changed.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-widest text-amber-light mb-1.5">Changed ({diff.fields_changed.length})</h3>
              <table className="w-full text-xs">
                <thead className="bg-surface-raised">
                  <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                    <th className="text-left px-2 py-1.5">Field</th>
                    <th className="text-left px-2 py-1.5">From</th>
                    <th className="text-left px-2 py-1.5">To</th>
                    <th className="text-left px-2 py-1.5">From source</th>
                    <th className="text-left px-2 py-1.5">To source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {diff.fields_changed.map(c => (
                    <tr key={c.field}>
                      <td className="px-2 py-1.5 font-mono text-text-primary">{c.field}</td>
                      <td className="px-2 py-1.5 font-mono text-rose-light">{formatValue(c.from)}</td>
                      <td className="px-2 py-1.5 font-mono text-emerald">{formatValue(c.to)}</td>
                      <td className="px-2 py-1.5 text-text-secondary">{c.from_source || '—'}</td>
                      <td className="px-2 py-1.5 text-text-secondary">{c.to_source || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {diff.fields_added.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-widest text-emerald mb-1.5">Added ({diff.fields_added.length})</h3>
              <ul className="text-xs font-mono text-text-secondary grid grid-cols-2 lg:grid-cols-4 gap-1">
                {diff.fields_added.map(f => <li key={f}>+ {f}</li>)}
              </ul>
            </div>
          )}

          {diff.fields_removed.length > 0 && (
            <div>
              <h3 className="text-[11px] uppercase tracking-widest text-rose-light mb-1.5">Removed ({diff.fields_removed.length})</h3>
              <ul className="text-xs font-mono text-text-secondary grid grid-cols-2 lg:grid-cols-4 gap-1">
                {diff.fields_removed.map(f => <li key={f}>− {f}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
